import * as React from 'react';

import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

// kpi.tsx — indicateur clé (DS Fase 3). Carte compacte : libellé mono uppercase,
// valeur en gros chiffre tabular-nums, delta optionnel (couleur TOUJOURS doublée
// d'une flèche texte — jamais couleur seule, ST-NFR-03), sparkline optionnelle.
// Tokens sémantiques uniquement. La grille (4-up) est portée par l'appelant.

/** Sens d'évolution du delta (le libellé texte + la flèche portent l'information). */
export type DeltaTone = 'up' | 'down' | 'flat';

const DELTA_TONE_CLASS: Record<DeltaTone, string> = {
  up: 'text-success',
  down: 'text-destructive',
  flat: 'text-muted-foreground',
};

/** Flèche texte doublant la couleur (WCAG 1.4.1 : jamais la couleur seule). */
const DELTA_TONE_ARROW: Record<DeltaTone, string> = {
  up: '▲',
  down: '▼',
  flat: '—',
};

/**
 * Sparkline SVG inline (aucune lib). ViewBox 100×28 étiré en largeur (responsive),
 * stroke non-scaling pour garder l'épaisseur du trait ; point final accentué ;
 * aire de fond optionnelle `fill-chart-2/10`. Purement décorative (aria-hidden) :
 * la valeur/le delta du KPI portent l'information. Rien à tracer sous 2 points.
 */
export function Sparkline({
  points,
}: {
  points: number[];
}): React.JSX.Element | null {
  if (points.length < 2) {
    return null;
  }
  const width = 100;
  const height = 28;
  const pad = 3;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;
  const stepX = (width - pad * 2) / (points.length - 1);
  const coords = points.map((p, i) => ({
    x: pad + i * stepX,
    y: pad + (height - pad * 2) * (1 - (p - min) / span),
  }));
  const line = coords.map((c) => `${String(c.x)},${String(c.y)}`).join(' ');
  const last = coords[coords.length - 1] ?? { x: width - pad, y: height - pad };
  const area = `${String(pad)},${String(height - pad)} ${line} ${String(
    width - pad,
  )},${String(height - pad)}`;

  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      preserveAspectRatio="none"
      className="h-7 w-full text-chart-2"
    >
      <polygon points={area} className="fill-chart-2/10" stroke="none" />
      <polyline
        points={line}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      <circle cx={last.x} cy={last.y} r={2.5} className="fill-chart-2" />
    </svg>
  );
}

export interface KpiProps {
  /** Libellé de l'indicateur (mono uppercase) — porteur d'information. */
  readonly label: string;
  /** Valeur formatée (déjà chaîne : l'appelant maîtrise l'unité/le format). */
  readonly value: string;
  /** Sous-libellé discret sous la valeur (contexte : « X soumis / Y invités »…). */
  readonly hint?: string;
  /** Variation textuelle (ex. « +12 % vs N-1 ») — optionnelle. */
  readonly delta?: string;
  /** Sens de la variation (couleur doublée d'une flèche texte). */
  readonly deltaTone?: DeltaTone;
  /** Série pour la sparkline — omise s'il n'existe aucune donnée de série. */
  readonly spark?: number[];
}

export function Kpi({
  label,
  value,
  hint,
  delta,
  deltaTone = 'flat',
  spark,
}: KpiProps): React.JSX.Element {
  return (
    <Card>
      <CardContent className="flex flex-col gap-2 p-4">
        <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <span className="text-2xl font-semibold tabular-nums">{value}</span>
        {hint !== undefined ? (
          <p className="text-xs text-muted-foreground">{hint}</p>
        ) : null}
        {delta !== undefined ? (
          <span
            className={cn(
              'inline-flex items-center gap-1 text-xs font-medium',
              DELTA_TONE_CLASS[deltaTone],
            )}
          >
            <span aria-hidden="true">{DELTA_TONE_ARROW[deltaTone]}</span>
            {delta}
          </span>
        ) : null}
        {spark !== undefined ? <Sparkline points={spark} /> : null}
      </CardContent>
    </Card>
  );
}
